import { GameLoop } from './GameLoop';
import { InputManager } from '../systems/InputManager';
import { SoundManager } from '../systems/SoundManager';
import { StatsManager } from '../systems/StatsManager';
import { AchievementManager } from '../systems/AchievementManager';
import { TitleScreen } from '../screens/TitleScreen';
import { GameScreen } from '../screens/GameScreen';
import { OptionsScreen } from '../screens/OptionsScreen';
import { AchievementScreen } from '../screens/AchievementScreen';
import { PixiRenderer } from '../pixi/PixiRenderer';
import { CONFIG } from '../config';

export const GameState = {
    TITLE: 'TITLE',
    OPTIONS: 'OPTIONS',
    PLAYING: 'PLAYING',
    ACHIEVEMENTS: 'ACHIEVEMENTS'
} as const;

export type GameState = typeof GameState[keyof typeof GameState];

interface BackgroundState {
    hue: number;
    angle: number;
}

interface Screen {
    init?(): void;
    update?(deltaTime: number): void;
    render?(): void;
    cleanup?(): void;
    onOrientationChange?(isLandscape: boolean, wasLandscape: boolean): void;
    isPaused?: boolean;
    syncScreenState?(): void;
    setPixiRenderer?(renderer: PixiRenderer): void;
}

type ScreensMap = {
    [key in GameState]: Screen;
};

// iOS Safari 페이지 복원을 위한 상태 저장 키
const GAME_STATE_KEY = 'daynight_game_state';

interface SavedGameState {
    currentState: GameState;
    timestamp: number;
}

export class Game {
    private pixiRenderer: PixiRenderer;
    private canvas: HTMLCanvasElement;
    private soundManager: SoundManager;
    private inputManager: InputManager;
    private backgroundState: BackgroundState;
    private statsManager: StatsManager;
    private achievementManager: AchievementManager;
    private optionsScreen: OptionsScreen;
    private screens: ScreensMap;
    public currentState: GameState;
    private currentScreen: Screen | null;
    private gameLoop: GameLoop;
    private initialized: boolean = false;
    private visibilityHandler: (() => void) | null = null;
    private pagehideHandler: ((e: PageTransitionEvent) => void) | null = null;
    private pageshowHandler: ((e: PageTransitionEvent) => void) | null = null;
    private blurHandler: (() => void) | null = null;
    private focusHandler: (() => void) | null = null;
    private freezeHandler: (() => void) | null = null;
    private resumeHandler: (() => void) | null = null;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.pixiRenderer = new PixiRenderer();
        this.soundManager = new SoundManager();
        this.inputManager = new InputManager(this.soundManager);

        // Shared background state (persists across screens)
        this.backgroundState = {
            hue: 0,
            angle: 0
        };

        // Initialize managers
        this.statsManager = new StatsManager();
        this.achievementManager = new AchievementManager();

        // Initialize screens (will be fully set up after PixiJS init)
        this.optionsScreen = new OptionsScreen(this as never);
        this.screens = {
            [GameState.TITLE]: new TitleScreen(this as never),
            [GameState.OPTIONS]: this.optionsScreen,
            [GameState.PLAYING]: new GameScreen(this as never) as unknown as Screen,
            [GameState.ACHIEVEMENTS]: new AchievementScreen(this as never)
        };

        this.currentState = GameState.TITLE;
        this.currentScreen = this.screens[this.currentState];

        // Initialize game loop
        this.gameLoop = new GameLoop(
            (deltaTime: number) => this.update(deltaTime),
            () => this.render()
        );

        // Handle page visibility (pause audio when app goes to background)
        this.setupVisibilityHandler();
    }

    async init(): Promise<void> {
        if (this.initialized) return;

        // Initialize PixiJS renderer
        const width = CONFIG.CANVAS_WIDTH;
        const height = CONFIG.CANVAS_HEIGHT;
        await this.pixiRenderer.init(this.canvas, width, height);

        // Set up input manager with renderer
        this.inputManager.setRenderer(this.pixiRenderer);

        // Handle orientation change
        this.pixiRenderer.onOrientationChange = (isLandscape: boolean, wasLandscape: boolean) => {
            this.onOrientationChange(isLandscape, wasLandscape);
        };

        // Initialize screens with PixiJS renderer
        this.initScreensWithPixi();

        // Initialize the first screen
        if (this.currentScreen && this.currentScreen.init) {
            this.currentScreen.init();
        }

        this.initialized = true;
    }

    private initScreensWithPixi(): void {
        // Pass PixiRenderer to screens that need it
        const titleScreen = this.screens[GameState.TITLE] as TitleScreen;
        if (titleScreen.setPixiRenderer) {
            titleScreen.setPixiRenderer(this.pixiRenderer);
        }

        const optionsScreen = this.screens[GameState.OPTIONS] as OptionsScreen;
        if (optionsScreen.setPixiRenderer) {
            optionsScreen.setPixiRenderer(this.pixiRenderer);
        }

        const gameScreen = this.screens[GameState.PLAYING] as unknown as GameScreen;
        if (gameScreen.setPixiRenderer) {
            gameScreen.setPixiRenderer(this.pixiRenderer);
        }

        const achievementScreen = this.screens[GameState.ACHIEVEMENTS] as AchievementScreen;
        if (achievementScreen.setPixiRenderer) {
            achievementScreen.setPixiRenderer(this.pixiRenderer);
        }
    }

    setupVisibilityHandler(): void {
        const handleHidden = () => {
            // 상태 저장 (iOS Safari 메모리 해제 대비)
            this.saveState();

            this.soundManager.pauseAll();
            // PixiJS ticker 일시정지
            this.pixiRenderer.pause();
            // Pause game if playing
            if (this.currentState === GameState.PLAYING && this.currentScreen) {
                this.currentScreen.isPaused = true;
                if (this.currentScreen.syncScreenState) {
                    this.currentScreen.syncScreenState();
                }
            }
        };

        const handleVisible = () => {
            this.soundManager.resumeAll();
            // PixiJS ticker 재개
            this.pixiRenderer.resume();
            // Game stays paused - user needs to manually resume
        };

        this.visibilityHandler = () => {
            if (document.hidden) {
                handleHidden();
            } else {
                handleVisible();
            }
        };

        // pagehide: 상태 저장 (bfcache 대응)
        this.pagehideHandler = (e: PageTransitionEvent) => {
            // persisted가 true면 bfcache에 저장됨
            this.saveState();
            handleHidden();
        };

        // pageshow: bfcache에서 복원 시 처리
        this.pageshowHandler = (e: PageTransitionEvent) => {
            if (e.persisted) {
                // bfcache에서 복원됨 - 오디오 컨텍스트 재초기화 필요할 수 있음
                console.log('[Game] Restored from bfcache');
                this.handleBfcacheRestore();
            } else {
                handleVisible();
            }
        };

        this.blurHandler = handleHidden;
        this.focusHandler = handleVisible;

        // freeze/resume 이벤트 (iOS 15+, Page Lifecycle API)
        this.freezeHandler = () => {
            console.log('[Game] Page frozen');
            this.saveState();
            handleHidden();
        };

        this.resumeHandler = () => {
            console.log('[Game] Page resumed');
            handleVisible();
        };

        document.addEventListener('visibilitychange', this.visibilityHandler);
        document.addEventListener('pagehide', this.pagehideHandler as EventListener);
        document.addEventListener('pageshow', this.pageshowHandler as EventListener);
        window.addEventListener('blur', this.blurHandler);
        window.addEventListener('focus', this.focusHandler);

        // freeze/resume 이벤트 (지원하는 브라우저만)
        document.addEventListener('freeze', this.freezeHandler);
        document.addEventListener('resume', this.resumeHandler);
    }

    /**
     * 게임 상태를 sessionStorage에 저장
     * iOS Safari가 메모리 압박으로 페이지를 종료해도 복원 가능
     */
    private saveState(): void {
        try {
            const state: SavedGameState = {
                currentState: this.currentState,
                timestamp: Date.now()
            };
            sessionStorage.setItem(GAME_STATE_KEY, JSON.stringify(state));
        } catch (e) {
            // sessionStorage 사용 불가 시 무시
        }
    }

    /**
     * 저장된 상태 불러오기
     */
    private loadState(): SavedGameState | null {
        try {
            const saved = sessionStorage.getItem(GAME_STATE_KEY);
            if (saved) {
                const state = JSON.parse(saved) as SavedGameState;
                // 5분 이내의 상태만 유효
                if (Date.now() - state.timestamp < 5 * 60 * 1000) {
                    return state;
                }
            }
        } catch (e) {
            // 파싱 실패 시 무시
        }
        return null;
    }

    /**
     * 저장된 상태 삭제
     */
    private clearState(): void {
        try {
            sessionStorage.removeItem(GAME_STATE_KEY);
        } catch (e) {
            // 무시
        }
    }

    /**
     * bfcache에서 복원 시 처리
     */
    private handleBfcacheRestore(): void {
        // 오디오 컨텍스트 재초기화
        this.soundManager.resumeAll();
        this.pixiRenderer.resume();

        // 게임 중이었다면 일시정지 상태 유지
        if (this.currentState === GameState.PLAYING && this.currentScreen) {
            // 이미 isPaused가 true일 것이므로 syncScreenState만 호출
            if (this.currentScreen.syncScreenState) {
                this.currentScreen.syncScreenState();
            }
        }
    }

    destroy(): void {
        // 이벤트 리스너 제거
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }
        if (this.pagehideHandler) {
            document.removeEventListener('pagehide', this.pagehideHandler as EventListener);
            this.pagehideHandler = null;
        }
        if (this.pageshowHandler) {
            document.removeEventListener('pageshow', this.pageshowHandler as EventListener);
            this.pageshowHandler = null;
        }
        if (this.blurHandler) {
            window.removeEventListener('blur', this.blurHandler);
            this.blurHandler = null;
        }
        if (this.focusHandler) {
            window.removeEventListener('focus', this.focusHandler);
            this.focusHandler = null;
        }
        if (this.freezeHandler) {
            document.removeEventListener('freeze', this.freezeHandler);
            this.freezeHandler = null;
        }
        if (this.resumeHandler) {
            document.removeEventListener('resume', this.resumeHandler);
            this.resumeHandler = null;
        }

        // 저장된 상태 정리
        this.clearState();

        // 현재 화면 정리
        if (this.currentScreen && this.currentScreen.cleanup) {
            this.currentScreen.cleanup();
        }

        // 하위 시스템 정리
        this.gameLoop.destroy();
        this.inputManager.destroy();
        this.pixiRenderer.destroy();
        this.soundManager.destroy();

        this.initialized = false;
    }

    onOrientationChange(isLandscape: boolean, wasLandscape: boolean): void {
        // Pause game if playing and transform positions
        if (this.currentState === GameState.PLAYING && this.currentScreen && this.currentScreen.onOrientationChange) {
            this.currentScreen.onOrientationChange(isLandscape, wasLandscape);
        }
    }

    start(): void {
        this.gameLoop.start();
    }

    setState(newState: GameState): void {
        if (this.currentState === newState) return;

        // Cleanup current screen
        if (this.currentScreen && this.currentScreen.cleanup) {
            this.currentScreen.cleanup();
        }

        // Clear PixiJS layers for new screen
        if (this.pixiRenderer.isInitialized()) {
            this.pixiRenderer.getLayers().clearAll();
        }

        // Change state
        this.currentState = newState;
        this.currentScreen = this.screens[newState];

        // Initialize new screen
        if (this.currentScreen && this.currentScreen.init) {
            this.currentScreen.init();
        }
    }

    update(deltaTime: number): void {
        // Update shared background state
        this.backgroundState.hue += deltaTime * 30;
        this.backgroundState.angle += deltaTime * 0.3;
        if (this.backgroundState.hue >= 360) {
            this.backgroundState.hue -= 360;
        }

        // Update current screen (input state from previous frame)
        if (this.currentScreen && this.currentScreen.update) {
            this.currentScreen.update(deltaTime);
        }

        // Update input at END of frame for next frame
        this.inputManager.update();
    }

    render(): void {
        // PixiJS handles rendering automatically via its ticker
        // Each screen manages its own PixiJS rendering

        // Render current screen (for screens that still need manual rendering)
        if (this.currentScreen && this.currentScreen.render) {
            this.currentScreen.render();
        }
    }

    getRenderer(): PixiRenderer {
        return this.pixiRenderer;
    }

    getInputManager(): InputManager {
        return this.inputManager;
    }

    getSoundManager(): SoundManager {
        return this.soundManager;
    }

    getOptions(): ReturnType<OptionsScreen['getOptions']> {
        const options = this.optionsScreen.getOptions();

        // 난이도 + 빅볼 모드 조합에 따라 배경 스타일 자동 결정
        // Easy: stripe, Normal: triangle, Hard: hexagon
        // BigBall Easy: diamond, BigBall Normal: sine, BigBall Hard: grid
        const bgStyleMap: Record<string, Record<string, 'stripe' | 'diamond' | 'triangle' | 'sine' | 'hexagon' | 'grid'>> = {
            easy: { normal: 'stripe', bigball: 'diamond' },
            normal: { normal: 'triangle', bigball: 'sine' },
            hard: { normal: 'hexagon', bigball: 'grid' }
        };

        const mode = options.bigballMode ? 'bigball' : 'normal';
        const bgStyle = bgStyleMap[options.difficulty]?.[mode] ?? 'stripe';

        return { ...options, bgStyle };
    }

    getStatsManager(): StatsManager {
        return this.statsManager;
    }

    getAchievementManager(): AchievementManager {
        return this.achievementManager;
    }

    getBackgroundState(): BackgroundState {
        return this.backgroundState;
    }
}
